// Meta Pixel tracking utilities
// This file contains helper functions to track custom events with Meta Pixel (client-side)
// and Meta Conversions API (server-side)

import { getEventContext } from "@/utils/eventContext";

// Make tracking functions available globally for inline scripts
if (typeof window !== "undefined") {
    (window as any).trackWhatsAppWidgetClick = () => {
        trackWhatsAppWidgetClick();
    };
}

/**
 * Generate a unique event ID for deduplication between client and server events
 */
const generateEventId = (): string => {
    const c = typeof crypto !== "undefined" ? (crypto as any) : undefined;
    if (c?.randomUUID) return c.randomUUID();
    return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

const readCookie = (name: string): string | undefined => {
    if (typeof document === "undefined") return undefined;
    for (const part of document.cookie.split(";")) {
        const eq = part.indexOf("=");
        if (eq === -1) continue;
        if (part.slice(0, eq).trim() === name) return part.slice(eq + 1).trim();
    }
    return undefined;
};

// A stable per-browser id, so repeat visits from the same person can be tied
// together by Meta even before they hand over an email or phone number.
const EXTERNAL_ID_KEY = "tss_external_id";
const getExternalId = (): string | undefined => {
    try {
        let id = window.localStorage.getItem(EXTERNAL_ID_KEY);
        if (!id) {
            id = generateEventId();
            window.localStorage.setItem(EXTERNAL_ID_KEY, id);
        }
        return id;
    } catch {
        // Private mode or storage disabled — match on the other signals.
        return undefined;
    }
};

/**
 * Send event to server-side Meta Conversions API
 */
const sendServerEvent = async (
    eventName: string,
    customData?: Record<string, any>,
    userData?: {
        email?: string;
        phone?: string;
        firstName?: string;
        lastName?: string;
    },
    eventId?: string,
    eventTime?: number
) => {
    try {
        await fetch("/api/meta-conversion", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                eventName,
                eventSourceUrl: window.location.href,
                customData,
                userData,
                eventId,
                eventTime,
                // Read here rather than server-side: these are the browser's
                // own first-party cookies and are the strongest match signal
                // we have for a visitor who has not filled in a form yet.
                fbc: readCookie("_fbc"),
                fbp: readCookie("_fbp"),
                externalId: getExternalId()
            })
        });
    } catch (error) {
        // Failed to send server-side event
    }
};

/**
 * Track custom event with Meta Pixel (client-side) and Conversions API (server-side)
 * @param eventName - Name of the event (e.g., 'Lead', 'CompleteRegistration', 'Contact')
 * @param eventParams - Optional parameters for the event
 * @param userData - Optional user data for server-side tracking (email, phone, etc.)
 */
export const trackMetaEvent = (
    eventName: string,
    eventParams?: Record<string, any>,
    userData?: {
        email?: string;
        phone?: string;
        firstName?: string;
        lastName?: string;
    }
) => {
    const eventId = generateEventId();
    const eventTime = Math.floor(Date.now() / 1000);
    // Caller-supplied params win, so an event can still override the context.
    const params = { ...getEventContext(), ...(eventParams ?? {}) };

    // Client-side tracking with Meta Pixel.
    // eventID belongs in fbq's fourth argument, NOT inside the parameter
    // object: passed as a parameter it is treated as ordinary custom data and
    // Meta never learns the browser and server events are the same event, so
    // every conversion gets counted twice (TD-151).
    if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq("track", eventName, params, {
            eventID: eventId
        });
    }

    // Server-side tracking with Conversions API
    sendServerEvent(eventName, params, userData, eventId, eventTime);
};

/**
 * Track custom event with Meta Pixel (for non-standard events)
 * @param eventName - Name of the custom event
 * @param eventParams - Optional parameters for the event
 */
export const trackMetaCustomEvent = (
    eventName: string,
    eventParams?: Record<string, any>
) => {
    const eventId = generateEventId();
    const eventTime = Math.floor(Date.now() / 1000);
    const params = { ...getEventContext(), ...(eventParams ?? {}) };

    // eventID goes in the fourth argument here too — see trackMetaEvent.
    if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq("trackCustom", eventName, params, {
            eventID: eventId
        });
    }

    // Server-side tracking
    sendServerEvent(eventName, params, undefined, eventId, eventTime);
};

// Specific tracking functions for your events

/**
 * Track when user views the book trial page
 */
export const trackBookTrialPageView = () => {
    trackMetaEvent("ViewContent", {
        content_name: "Book Free Trial Page",
        content_category: "Trial Booking"
    });
};

/**
 * Track when user completes trial booking (thank you page)
 */
export const trackBookTrialComplete = (
    bookingData?: {
        pool?: string;
        date?: string;
        numberOfChildren?: string;
    },
    userData?: {
        email?: string;
        phone?: string;
        firstName?: string;
    }
) => {
    // One booking is one conversion. This previously fired CompleteRegistration
    // AND Lead for the same action, so every trial booking was counted twice as
    // a standard event — inflating whichever of the two an ad set optimised on.
    // Lead now means a contact-form enquiry only (see trackLeadFormSubmit).
    //
    // TD-149: the name is `Schedule`, not `CompleteRegistration`. The static
    // landing pages (public/sg/guarantee-thank-you.html, thank-you.html) have
    // always fired Schedule for this same action, so the two funnels were
    // reporting one conversion under two names and no ad set could see both.
    // `Schedule` is the name the TD-146 spec assigns to Trial Arranged — see
    // the note on trackTrialBooked in utils/ga4.ts — and it is also Meta's
    // standard event for booking an appointment.
    trackMetaEvent(
        "Schedule",
        {
            content_name: "Free Trial Booking",
            status: "completed",
            ...bookingData
        },
        userData
    );
};

/**
 * Track when user submits contact/lead form
 */
export const trackLeadFormSubmit = (formType: string = "Contact Form") => {
    trackMetaEvent("Lead", {
        content_name: formType,
        content_category: "Lead Generation"
    });
};

// TD-154: pressing a WhatsApp link or opening the chat widget is NOT a
// conversion — it only means someone was handed off to WhatsApp, and the
// measured gap is enormous (438 widget opens produced ~15 clicks, TD-130).
// The conversion is an inbound message actually arriving in WATI, which the
// site cannot observe; it is raised from WATI's side (TD-131).
//
// So these stay as diagnostic custom events and no longer fire Meta's standard
// `Contact` event. That deliberately stops them counting as conversions.

/**
 * Diagnostic: user clicked a WhatsApp link. Not a conversion.
 */
export const trackWhatsAppLinkClick = (source: string = "Link") => {
    trackMetaCustomEvent("WhatsAppClick", {
        source: source
    });
};

/**
 * Diagnostic: user opened the WhatsApp chat widget. Not a conversion.
 */
export const trackWhatsAppWidgetClick = () => {
    trackMetaCustomEvent("WhatsAppWidgetOpen", {
        source: "Widget"
    });
};
